import { DataTable } from '@/shared/components/tables/DataTable';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { FindPersonFiltersDto } from '@/api.types.extra';
import { ReadPersonDto } from '@/api.types';
import { readPerson } from '../../person.service';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

type TipoPessoaIndex = 'todos' | 'defensor' | 'estagiario' | 'colaborador' | 'usuario';
type StatusAtivoFiltro = 'todos' | 'ativos' | 'inativos';

interface Props {
  tipo: TipoPessoaIndex;
  columns: import('@/shared/components/tables/DataTable').Column<ReadPersonDto>[];
}

function onlyDigits(v: string) {
  return v.replace(/\D/g, '');
}

function isAtivoByTipo(row: ReadPersonDto, tipo: TipoPessoaIndex) {
  switch (tipo) {
    case 'defensor':
      return row.defensor?.ativo ?? true;
    case 'estagiario':
      return row.estagiario?.ativo ?? true;
    case 'usuario':
      return row.usuario?.ativo ?? true;
    case 'colaborador':
      return (row as any)?.colaborador?.ativo ?? true;
    default:
      return true;
  }
}

export function PeopleDataTable({ tipo, columns }: Props) {
  const tableState = useDataTableState('nome');

  const [registros, setRegistros] = useState<[ReadPersonDto[], number]>();
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [statusAtivo, setStatusAtivo] = useState<StatusAtivoFiltro>('todos');

  const computedCpf = useMemo(() => onlyDigits(cpf), [cpf]);

  const needsAtivoFilter = tipo !== 'todos' && statusAtivo !== 'todos';
  const fetchModeServerSide = !needsAtivoFilter;
  const pageKey = fetchModeServerSide ? tableState.currentPage : 1;
  const pageSizeKey = fetchModeServerSide ? tableState.pageSize : 0;

  async function handleBuscar() {
    const filters: FindPersonFiltersDto = {
      skip: fetchModeServerSide ? (tableState.currentPage - 1) * tableState.pageSize : 0,
      take: fetchModeServerSide ? tableState.pageSize : 5000,
      nome: nome.trim() || undefined,
      cpf: computedCpf.trim() || undefined,
      email: email.trim() || undefined,
      comTelefone: true,

      comDefensor: tipo === 'defensor',
      isDefensor: tipo === 'defensor',

      comEstagiario: tipo === 'estagiario',
      isEstagiario: tipo === 'estagiario',
      comEstagios: tipo === 'estagiario',

      comUsuario: tipo === 'usuario',
      isUsuario: tipo === 'usuario',

      comColaborador: tipo === 'colaborador',
      isColaborador: tipo === 'colaborador',

      // Mantém o comportamento atual do index (backend parece interpretar como direção).
      orderBy: tableState.sortConfig.direction,
    };

    try {
      const res = await readPerson(filters);
      setRegistros(res);
    } catch (error) {
      toast.error(String(error));
    }
  }

  useEffect(() => {
    handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tipo,
    pageKey,
    pageSizeKey,
    tableState.sortConfig?.key,
    tableState.sortConfig?.direction,
    nome,
    computedCpf,
    email,
    statusAtivo,
  ]);

  const filteredByAtivo = useMemo(() => {
    if (!registros) return undefined;
    if (tipo === 'todos') return registros;
    if (statusAtivo === 'todos') return registros;

    const wantAtivo = statusAtivo === 'ativos';
    const rows = registros[0].filter((r) => isAtivoByTipo(r, tipo) === wantAtivo);
    return [rows, rows.length] as [ReadPersonDto[], number];
  }, [registros, statusAtivo, tipo]);

  return (
    <>
      {(filteredByAtivo || registros) && (
        <DataTable
          key={tipo}
          columns={columns}
          data={(filteredByAtivo ?? registros)?.[0]}
          initialSortKey="nome"
          serverSide={fetchModeServerSide}
          states={tableState}
          totalRecords={(filteredByAtivo ?? registros)?.[1]}
          showFilter={false}
          renderFilters={() => (
            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: 220 }}
                placeholder="Nome..."
                value={nome}
                onChange={(e) => {
                  setNome(e.target.value);
                  tableState.setCurrentPage(1);
                }}
              />

              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: 180 }}
                placeholder="CPF..."
                value={cpf}
                onChange={(e) => {
                  setCpf(e.target.value);
                  tableState.setCurrentPage(1);
                }}
              />

              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: 240 }}
                placeholder="E-mail..."
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  tableState.setCurrentPage(1);
                }}
              />

              {tipo !== 'todos' && (
                <select
                  className="form-select form-select-sm"
                  style={{ width: 180 }}
                  value={statusAtivo}
                  onChange={(e) => {
                    setStatusAtivo(e.target.value as StatusAtivoFiltro);
                    tableState.setCurrentPage(1);
                  }}
                  title="Filtrar por status (ativo/inativo)."
                >
                  <option value="todos">Ativos e inativos</option>
                  <option value="ativos">Somente ativos</option>
                  <option value="inativos">Somente inativos</option>
                </select>
              )}
            </div>
          )}
        />
      )}
    </>
  );
}

